import {
  Box, Grid, Card, CardContent, Typography, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Avatar, Stack, Divider,
} from '@mui/material';
import {
  Business, TrendingUp, People, ConfirmationNumber, AccountBalance,
  Warning, Store, Group, Receipt, PersonOff,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import api from '../../api/axios';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const statusColors = { active: 'success', trial: 'info', suspended: 'error', expired: 'default' };

const StatCard = ({ title, value, sub, icon, color = 'primary', onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      height: '100%',
      transition: 'all .18s',
      '&:hover': onClick ? { boxShadow: 6, transform: 'translateY(-2px)' } : {},
    }}
  >
    <CardContent sx={{ p: '14px 16px 14px 16px !important' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</Typography>
          <Typography fontWeight={700} sx={{ fontSize: 22, lineHeight: 1.3, whiteSpace: 'nowrap', mt: 0.3 }}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{sub}</Typography>}
        </Box>
        <Box sx={{ p: 1, bgcolor: `${color}.main`, borderRadius: 2, color: '#fff', flexShrink: 0, display: 'flex', '& svg': { fontSize: 22 } }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

export default function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/superadmin/dashboard')
      .then(r => setData(r.data.data))
      .catch(e => setErr(e.response?.data?.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress size={48} /></Box>;
  if (err) return <Alert severity="error">{err}</Alert>;

  const { counts, revenue, infra, monthlyRev, planDist, topClients, clientGrowth, recentActivity, expiring } = data;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={1}>Platform Overview</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>SaaS Super Admin — Pakistan POS</Typography>

      {expiring?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
          <strong>{expiring.length} subscription{expiring.length > 1 ? 's' : ''} expiring</strong> in the next 14 days.{' '}
          <Typography component="span" variant="body2" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/superadmin/tenants')}>View tenants →</Typography>
        </Alert>
      )}

      {/* Stat Cards — 2 rows of 5 */}
      <Grid container spacing={2} mb={3}>
        {[
          { title: 'Total Clients',  value: counts?.total || 0,            icon: <Business />,           color: 'primary',   onClick: () => navigate('/superadmin/tenants') },
          { title: 'Active',         value: counts?.active || 0,           icon: <Store />,              color: 'success' },
          { title: 'Trial',          value: counts?.trial || 0,            icon: <People />,             color: 'warning' },
          { title: 'Expired',        value: counts?.expired || 0,          icon: <Warning />,            color: 'error' },
          { title: 'Suspended',      value: counts?.suspended || 0,        icon: <PersonOff />,          color: 'error' },
          { title: 'MRR',            value: `Rs.${fmt(revenue?.mrr)}`,     icon: <TrendingUp />,         color: 'info' },
          { title: 'ARR',            value: `Rs.${fmt(revenue?.arr)}`,     icon: <AccountBalance />,     color: 'secondary' },
          { title: 'Revenue Total',  value: `Rs.${fmt(revenue?.all_time)}`,icon: <AccountBalance />,     color: 'success',   onClick: () => navigate('/superadmin/billing') },
          { title: 'Total Users',    value: fmt(infra?.total_users),       icon: <Group />,              color: 'primary' },
          { title: 'Open Tickets',   value: infra?.open_tickets || 0,      icon: <ConfirmationNumber />, color: 'error',     onClick: () => navigate('/superadmin/tickets') },
        ].map(c => (
          <Grid item xs={6} sm={4} md={2.4} key={c.title}><StatCard {...c} /></Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Monthly Revenue */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Monthly Revenue (12M)</Typography>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={monthlyRev}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={v => [`Rs.${fmt(v)}`, 'Revenue']} />
                  <Bar dataKey="revenue" fill="#6366f1" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Plan Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Plan Distribution</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={planDist} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, count }) => count > 0 ? `${name}: ${count}` : ''}>
                    {planDist?.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Client Growth */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Client Growth (12M)</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={clientGrowth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="new_clients" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} name="New Clients" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Clients */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Top Clients by Revenue</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell align="right">Total Paid</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topClients?.slice(0, 6).map(c => (
                    <TableRow key={c.name} hover sx={{ cursor: 'pointer' }} onClick={() => navigate('/superadmin/tenants')}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: '#6366f1' }}>{c.name[0]}</Avatar>
                          <Typography variant="body2">{c.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight={600}>Rs.{fmt(c.total_paid)}</Typography></TableCell>
                      <TableCell><Chip label={c.status} size="small" color={statusColors[c.status] || 'default'} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        {/* Expiring Subscriptions */}
        {expiring?.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card sx={{ border: '1px solid', borderColor: 'warning.light' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} mb={2} color="warning.main">⚠ Expiring Subscriptions</Typography>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Tenant</TableCell><TableCell>Expires</TableCell><TableCell align="right">Days Left</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {expiring.map(t => (
                      <TableRow key={t.id} hover>
                        <TableCell>{t.name}</TableCell>
                        <TableCell>{new Date(t.subscription_ends_at).toLocaleDateString('en-GB')}</TableCell>
                        <TableCell align="right">
                          <Chip label={`${t.days_left}d`} size="small" color={t.days_left <= 3 ? 'error' : 'warning'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recent Activity */}
        <Grid item xs={12} md={expiring?.length > 0 ? 6 : 12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Recent Activity</Typography>
              <Stack spacing={1} divider={<Divider />}>
                {recentActivity?.slice(0, 8).map((a, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{a.description}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.user_name}{a.tenant_name ? ` — ${a.tenant_name}` : ''}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 2 }}>
                      {new Date(a.created_at).toLocaleString('en-PK', { month:'short', day:'2-digit', hour:'2-digit', minute:'2-digit' })}
                    </Typography>
                  </Box>
                ))}
                {(!recentActivity || recentActivity.length === 0) && <Typography variant="body2" color="text.secondary">No recent activity</Typography>}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
